import { useCallback } from "react";
import { toast } from "@heroui/react";
import { useTranslation } from "react-i18next";
import html2canvas from "html2canvas";
import html2pdf from "html2pdf.js";
import { Document, Packer, Paragraph, ImageRun } from "docx";
import PptxGenJS from "pptxgenjs";
import { useBrand, useUser, useAuth } from "@webuild-ai/react-auth";

import { Artefact, LANGUAGE_FILE_EXTENSIONS, ArtefactType } from "@/types/artefacts";
import { ARTEFACT_CONFIG, ARTEFACT_ERRORS } from "@/config/artefacts";
import { escapeHtml } from "@/utils/htmlEscape";
import { parseTableContent, tableToCSV, tableToJSON, tableToMarkdown } from "@/utils/tableUtils";
import { DownloadFormat } from "@/utils/artefactFormatDetection";
import { convertMarkdownToDocx } from "@/utils/markdownToDocx";
import { renderMarkdownToPdf } from "@/utils/pdfRenderer";
import { convertMarkdownToPptxSlides } from "@/utils/markdownToPptx";
import { batchExportToExcel } from "@/utils/artefactExport/excelExport";
import { escapeCssSelector, sanitizeUnsupportedColors } from "@/utils/styleUtils";
import { resolveAccessibleIframe } from "@/utils/artefactExport/htmlToImage";
import { useTheme } from "@/hooks/use-theme";
import { useUIPreferences } from "@/stores/UIPreferencesStore";
import { DEFAULT_FONT, extractPrimaryFont } from "@/utils/fontUtils";
import { logger } from "@/telemetry";
import {
  resolveTemplate,
  applyWordTemplate,
  prepareTemplateData,
  applyPptxTemplate,
  preparePptxTemplateData,
} from "@/utils/templateEngine";

interface CopyOptions {
  includeFilename?: boolean;
  includeMetadata?: boolean;
}

/**
 * Generate filename with timestamp
 * Sanitizes filename to prevent path traversal attacks
 */
const generateFilename = (artefact: Artefact, extension: string): string => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-").split("T");
  const dateStr = timestamp[0];
  const timeStr = timestamp[1].substring(0, 8); // Remove milliseconds

  // Sanitize filename: remove path separators and special characters
  // Only allow alphanumeric, spaces, hyphens, and underscores
  const baseFilename = artefact.title
    .replace(/[/\\]/g, "") // Remove path separators
    .replace(/\.\./g, "") // Remove parent directory references
    .toLowerCase()
    .replace(/[^a-z0-9\s_-]/g, "_") // Replace disallowed characters with underscore
    .replace(/\s+/g, "_") // Replace spaces with underscores
    .replace(/_+/g, "_") // Collapse multiple underscores
    .substring(0, 100); // Limit length to prevent issues

  return `${baseFilename}_${dateStr}_${timeStr}.${extension}`;
};

/**
 * Hook for common artefact actions (copy, download, refresh)
 */
export const useArtefactActions = () => {
  const { t } = useTranslation();
  const { brand } = useBrand();
  const { userProfile } = useUser();
  const { user } = useAuth();
  const { theme } = useTheme();
  const { exportFontFamily, exportPageSize } = useUIPreferences();
  // Resolve author reactively — the callback is recreated when the profile
  // loads, so exports always use the latest known name.
  const getAuthorName = useCallback(
    () => userProfile?.name || user?.name || brand.name,
    [userProfile?.name, user?.name, brand.name],
  );

  /**
   * Get the effective font family for Word exports based on user preference
   * "brand" = use brand font, otherwise use the explicitly selected font.
   * Uses extractPrimaryFont() to handle CSS font-family strings like "Raleway,sans-serif"
   * since the docx library expects a single font name, not a font stack.
   */
  const getExportFontFamily = useCallback((): string => {
    if (exportFontFamily === "brand") {
      return extractPrimaryFont(brand.fontFamily || DEFAULT_FONT);
    }

    return extractPrimaryFont(exportFontFamily);
  }, [exportFontFamily, brand.fontFamily]);

  // HTML-specific image copy function (defined first so handleCopy can reference it)
  const handleCopyAsImage = useCallback(
    async (artefact: Artefact) => {
      // Show loading toast
      toast(t("Capturing image..."), {
        description: t("Please wait while we capture the content"),
      });

      try {
        // Resolve an accessible iframe (creates temp one if sandbox blocks contentDocument)
        const { iframe, cleanup } = await resolveAccessibleIframe(artefact);
        const doc = iframe.contentDocument!;

        try {
          // Fast path: try native canvas extraction (Chart.js, etc.)
          const nativeCanvas = doc.querySelector("canvas") as HTMLCanvasElement | null;
          let captureCanvas: HTMLCanvasElement;

          if (nativeCanvas && nativeCanvas.width > 0 && nativeCanvas.height > 0) {
            try {
              nativeCanvas.toDataURL(); // Taint check
              captureCanvas = nativeCanvas;
            } catch {
              // Tainted canvas — fall back to html2canvas
              captureCanvas = await html2canvas(doc.body, {
                scale: 3,
                backgroundColor: null,
                logging: false,
                useCORS: true,
                allowTaint: false,
                removeContainer: true,
                imageTimeout: 15000,
                windowWidth: doc.body.scrollWidth,
                windowHeight: doc.body.scrollHeight,
                onclone: (clonedDoc: globalThis.Document) => {
                  sanitizeUnsupportedColors(clonedDoc);
                },
              });
            }
          } else {
            // No native canvas — use html2canvas
            captureCanvas = await html2canvas(doc.body, {
              scale: 3,
              backgroundColor: null,
              logging: false,
              useCORS: true,
              allowTaint: false,
              removeContainer: true,
              imageTimeout: 15000,
              windowWidth: doc.body.scrollWidth,
              windowHeight: doc.body.scrollHeight,
              onclone: (clonedDoc: globalThis.Document) => {
                sanitizeUnsupportedColors(clonedDoc);
              },
            });
          }

          // Convert canvas to blob
          const blob = await new Promise<Blob>((resolve, reject) => {
            captureCanvas.toBlob((result) => {
              if (result) {
                resolve(result);
              } else {
                reject(new Error("Failed to convert canvas to blob"));
              }
            }, "image/png");
          });

          // Copy image to clipboard using ClipboardItem API
          const item = new ClipboardItem({ "image/png": blob });

          await navigator.clipboard.write([item]);

          // Remove loading toast and show success
          toast.success(t("Image copied!"), {
            description: t("Content has been copied as an image to your clipboard"),
          });
        } finally {
          cleanup();
        }
      } catch (error) {
        logger.error("❌ Failed to copy as image", error);

        // Show error toast
        toast.danger(t("Copy failed"), {
          description: t("Unable to copy content as image. Try downloading instead."),
        });
      }
    },
    [t],
  );

  const handleCopy = useCallback(
    async (artefact: Artefact, options: CopyOptions = {}) => {
      // Delegate HTML artefacts to image copy
      if (artefact.type === ArtefactType.HTML) {
        return handleCopyAsImage(artefact);
      }

      try {
        let contentToCopy = artefact.content;

        // Add filename header if requested
        if (options.includeFilename && artefact.title) {
          const fileExtension =
            artefact.metadata?.fileExtension ||
            LANGUAGE_FILE_EXTENSIONS[artefact.language || ""] ||
            ARTEFACT_CONFIG.fileExtensions.fallback;

          const filename = `${artefact.title.toLowerCase().replace(/[^a-z0-9]/g, "_")}.${fileExtension}`;
          const timestamp = new Date().toLocaleString();

          contentToCopy = `// File: ${filename}\n// Generated: ${timestamp}\n\n${artefact.content}`;
        }

        // Add metadata if requested
        if (options.includeMetadata) {
          const language = artefact.language || "text";
          const lineCount = artefact.metadata?.lineCount || artefact.content.split("\n").length;

          const metadataHeader = `// Language: ${language}\n// Lines: ${lineCount}\n// Copied: ${new Date().toLocaleString()}\n\n`;

          contentToCopy = metadataHeader + contentToCopy;
        }

        await navigator.clipboard.writeText(contentToCopy);

        // Show success toast notification
        const toastMessage =
          options.includeFilename || options.includeMetadata
            ? t("Content with metadata has been copied to your clipboard")
            : t("Content has been copied to your clipboard");

        toast.success(toastMessage);

        // Copy operation completed successfully
      } catch (error) {
        logger.error(`❌ ${ARTEFACT_ERRORS.copy}`, error);

        // Show error toast notification
        toast.danger(t("Copy failed"), { description: t("Unable to copy content to clipboard") });
      }
    },
    [handleCopyAsImage, t],
  );

  const handleDownload = useCallback(
    (artefact: Artefact) => {
      try {
        const fileExtension =
          artefact.metadata?.fileExtension ||
          LANGUAGE_FILE_EXTENSIONS[artefact.language || ""] ||
          ARTEFACT_CONFIG.fileExtensions.fallback;

        // Enhanced filename with timestamp for uniqueness
        const timestamp = new Date().toISOString().replace(/[:.]/g, "-").split("T");
        const dateStr = timestamp[0];
        const timeStr = timestamp[1].substring(0, 8); // Remove milliseconds
        const baseFilename = artefact.title.toLowerCase().replace(/[^a-z0-9]/g, "_");
        const filename = `${baseFilename}_${dateStr}_${timeStr}.${fileExtension}`;

        const blob = new Blob([artefact.content], { type: "text/plain;charset=utf-8" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");

        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);

        // Show success toast notification
        toast.success(t("File downloaded!"), {
          description: t("Saved as {{filename}}", { filename }),
        });

        // Download operation completed successfully
      } catch (error) {
        logger.error(`❌ ${ARTEFACT_ERRORS.download}`, error);

        // Show error toast notification
        toast.danger(t("Download failed"), { description: t("Unable to download the file") });
      }
    },
    [t],
  );

  const handleDownloadAsImage = useCallback(
    async (artefact: Artefact) => {
      // Show loading toast
      toast(t("Capturing image..."), {
        description: t("Please wait while we prepare your download"),
      });

      // Track cleanup for temporary iframes (HTML artefacts with opaque origins)
      let iframeCleanup: (() => void) | null = null;

      try {
        // Find the element by artefact ID (escape for CSS selector safety)
        const escapedId = escapeCssSelector(artefact.id);

        // For HTML artefacts, look for iframe; for others (TABLE, CODE, DOCUMENT), look for div
        let elementToCapture: HTMLElement | null = null;
        let captureWidth: number;
        let captureHeight: number;

        if (artefact.type === ArtefactType.HTML) {
          // HTML artefacts render in iframes — resolve an accessible one
          const resolved = await resolveAccessibleIframe(artefact);

          iframeCleanup = resolved.cleanup;
          const doc = resolved.iframe.contentDocument!;

          // Fast path: try native canvas extraction (Chart.js, etc.)
          const nativeCanvas = doc.querySelector("canvas") as HTMLCanvasElement | null;

          if (nativeCanvas && nativeCanvas.width > 0 && nativeCanvas.height > 0) {
            try {
              nativeCanvas.toDataURL(); // Taint check
              const nativeBlob = await new Promise<Blob>((resolve, reject) => {
                nativeCanvas.toBlob((result) => {
                  if (result) resolve(result);
                  else reject(new Error("Failed to convert canvas to blob"));
                }, "image/png");
              });
              const timestamp = new Date().toISOString().replace(/[:.]/g, "-").split("T");
              const baseFilename = artefact.title.toLowerCase().replace(/[^a-z0-9]/g, "_");
              const filename = `${baseFilename}_${timestamp[0]}_${timestamp[1].substring(0, 8)}.png`;
              const url = URL.createObjectURL(nativeBlob);
              const link = document.createElement("a");

              link.href = url;
              link.download = filename;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);

              toast.success(t("Image downloaded!"), {
                description: t("Saved as {{filename}}", { filename }),
              });

              return; // Done — skip html2canvas path
            } catch {
              // Tainted canvas — fall through to html2canvas
            }
          }

          elementToCapture = doc.body;
          captureWidth = doc.body.scrollWidth;
          captureHeight = doc.body.scrollHeight;
        } else if (artefact.type === ArtefactType.TABLE) {
          // TABLE artefacts have nested containers with overflow constraints.
          // Target the table viewer wrapper specifically — the outer
          // ArtefactsList card also carries `data-artefact-id` and would
          // otherwise be matched first.
          const container = document.querySelector(
            `[data-artefact-table-id="${escapedId}"]`,
          ) as HTMLElement;

          if (!container) {
            throw new Error("Element not found for artefact");
          }

          // Find the actual table element inside the container
          const tableElement = container.querySelector("table") as HTMLElement;

          if (tableElement) {
            // Use the table's natural dimensions (not constrained by parent overflow)
            captureWidth = Math.max(tableElement.scrollWidth, tableElement.offsetWidth, 800);
            captureHeight = Math.max(tableElement.scrollHeight, tableElement.offsetHeight, 400);
          } else {
            // Fallback to container dimensions if no table found
            captureWidth = container.scrollWidth;
            captureHeight = container.scrollHeight;
          }

          elementToCapture = container;
        } else {
          // CODE, DOCUMENT artefacts render directly in the DOM
          const container = document.querySelector(
            `[data-artefact-id="${escapedId}"]`,
          ) as HTMLElement;

          if (!container) {
            throw new Error("Element not found for artefact");
          }

          elementToCapture = container;
          captureWidth = container.scrollWidth;
          captureHeight = container.scrollHeight;
        }

        if (!elementToCapture) {
          throw new Error("No element available to capture");
        }

        // Capture the element as canvas with high quality
        const canvas = await html2canvas(elementToCapture, {
          scale: 3, // High DPI for crisp images (3x device pixel ratio)
          backgroundColor: "#ffffff", // White background for better image quality
          logging: false,
          useCORS: true,
          allowTaint: false,
          removeContainer: true,
          imageTimeout: 15000,
          windowWidth: captureWidth,
          windowHeight: captureHeight,
          // Sanitize unsupported CSS colour functions and remove height constraints before capture
          onclone: (clonedDoc: globalThis.Document) => {
            sanitizeUnsupportedColors(clonedDoc);

            // For TABLE artefacts, remove all constraints to capture full content
            if (artefact.type === ArtefactType.TABLE) {
              // Find and remove max-height, overflow, and width constraints from the cloned element
              const clonedContainer = clonedDoc.querySelector(
                `[data-artefact-table-id="${escapedId}"]`,
              ) as HTMLElement;

              if (clonedContainer) {
                // Remove all constraints from the container and its children
                const allElements = clonedContainer.querySelectorAll("*");

                allElements.forEach((el) => {
                  const element = el as HTMLElement;

                  // Remove height constraints
                  element.style.maxHeight = "none";
                  element.style.height = "auto";

                  // Remove overflow constraints
                  element.style.overflow = "visible";
                  element.style.overflowX = "visible";
                  element.style.overflowY = "visible";

                  // Remove width constraints that truncate content
                  if (element.classList.contains("truncate")) {
                    element.style.maxWidth = "none";
                    element.style.overflow = "visible";
                    element.style.textOverflow = "clip";
                    element.style.whiteSpace = "normal";
                  }

                  // Ensure table cells show full content
                  if (element.tagName === "TD" || element.tagName === "TH") {
                    element.style.maxWidth = "none";
                    element.style.whiteSpace = "nowrap";
                  }
                });

                // Update the container itself
                clonedContainer.style.maxHeight = "none";
                clonedContainer.style.height = "auto";
                clonedContainer.style.overflow = "visible";
                clonedContainer.style.width = "auto";
                clonedContainer.style.minWidth = "fit-content";
              }
            }
          },
        });

        // Convert canvas to blob
        const blob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob((result) => {
            if (result) {
              resolve(result);
            } else {
              reject(new Error("Failed to convert canvas to blob"));
            }
          }, "image/png");
        });

        // Generate filename with timestamp
        const timestamp = new Date().toISOString().replace(/[:.]/g, "-").split("T");
        const dateStr = timestamp[0];
        const timeStr = timestamp[1].substring(0, 8); // Remove milliseconds
        const baseFilename = artefact.title.toLowerCase().replace(/[^a-z0-9]/g, "_");
        const filename = `${baseFilename}_${dateStr}_${timeStr}.png`;

        // Create download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);

        // Show success toast
        toast.success(t("Image downloaded!"), {
          description: t("Saved as {{filename}}", { filename }),
        });
      } catch (error) {
        logger.error("❌ Failed to download as image", error);

        // Show error toast
        toast.danger(t("Download failed"), {
          description: t("Unable to download content as image"),
        });
      } finally {
        iframeCleanup?.();
      }
    },
    [t],
  );

  const handleRefresh = useCallback((artefactId: string) => {
    try {
      const escapedId = escapeCssSelector(artefactId);
      const iframe = document.querySelector(
        `iframe[data-artefact-id="${escapedId}"]`,
      ) as HTMLIFrameElement;

      if (iframe) {
        const currentSrc = iframe.getAttribute("srcdoc");

        iframe.setAttribute("srcdoc", "");
        setTimeout(() => {
          iframe.setAttribute("srcdoc", currentSrc || "");
        }, ARTEFACT_CONFIG.iframe.refreshDelay);
      }
    } catch (error) {
      logger.error("❌ Failed to refresh artefact", error);
    }
  }, []);

  const formatTimestamp = useCallback((timestamp: string): string => {
    const date = new Date(timestamp);

    return date.toLocaleString();
  }, []);

  // Convenience methods for different copy formats
  const handleCopyPlain = useCallback((artefact: Artefact) => handleCopy(artefact), [handleCopy]);

  const handleCopyWithFilename = useCallback(
    (artefact: Artefact) => handleCopy(artefact, { includeFilename: true }),
    [handleCopy],
  );

  const handleCopyWithMetadata = useCallback(
    (artefact: Artefact) => handleCopy(artefact, { includeMetadata: true }),
    [handleCopy],
  );

  // Table-specific export functions
  const handleCopyAsCSV = useCallback(
    async (artefact: Artefact) => {
      try {
        if (artefact.type !== ArtefactType.TABLE) {
          throw new Error("Artefact is not a table");
        }

        const tableData = parseTableContent(artefact.content);

        if (!tableData) {
          throw new Error("Failed to parse table data");
        }

        const csvContent = tableToCSV(tableData.headers, tableData.rows);

        await navigator.clipboard.writeText(csvContent);

        toast.success(t("CSV copied!"), { description: t("Table data copied as CSV format") });
      } catch (error) {
        logger.error("❌ Failed to copy as CSV", error);
        toast.danger(t("Copy failed"), { description: t("Unable to copy table as CSV") });
      }
    },
    [t],
  );

  const handleCopyAsJSON = useCallback(
    async (artefact: Artefact) => {
      try {
        if (artefact.type !== ArtefactType.TABLE) {
          throw new Error("Artefact is not a table");
        }

        const tableData = parseTableContent(artefact.content);

        if (!tableData) {
          throw new Error("Failed to parse table data");
        }

        const jsonContent = tableToJSON(tableData.rows);

        await navigator.clipboard.writeText(jsonContent);

        toast.success(t("JSON copied!"), { description: t("Table data copied as JSON format") });
      } catch (error) {
        logger.error("❌ Failed to copy as JSON", error);
        toast.danger(t("Copy failed"), { description: t("Unable to copy table as JSON") });
      }
    },
    [t],
  );

  const handleDownloadAsCSV = useCallback(
    (artefact: Artefact) => {
      try {
        if (artefact.type !== ArtefactType.TABLE) {
          throw new Error("Artefact is not a table");
        }

        const tableData = parseTableContent(artefact.content);

        if (!tableData) {
          throw new Error("Failed to parse table data");
        }

        const csvContent = tableToCSV(tableData.headers, tableData.rows);

        // Generate smart filename based on content
        const baseFilename = artefact.title.toLowerCase().replace(/[^a-z0-9]/g, "_");
        const timestamp = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
        const filename = `${baseFilename}_${timestamp}.csv`;

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");

        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);

        toast.success(t("CSV downloaded!"), {
          description: t("Saved as {{filename}}", { filename }),
        });
      } catch (error) {
        logger.error("❌ Failed to download CSV", error);
        toast.danger(t("Download failed"), { description: t("Unable to download table as CSV") });
      }
    },
    [t],
  );

  // Document-specific export functions
  const handleCopyAsMarkdown = useCallback(
    async (artefact: Artefact) => {
      try {
        if (artefact.type !== ArtefactType.DOCUMENT) {
          throw new Error("Artefact is not a document");
        }

        // For documents, the content is already in markdown format
        await navigator.clipboard.writeText(artefact.content);

        toast.success(t("Markdown copied!"), {
          description: t("Document copied as Markdown format"),
        });
      } catch (error) {
        logger.error("❌ Failed to copy as Markdown", error);
        toast.danger(t("Copy failed"), { description: t("Unable to copy document as Markdown") });
      }
    },
    [t],
  );

  const handleDownloadAsMarkdown = useCallback(
    (artefact: Artefact) => {
      try {
        let markdownContent: string;

        if (artefact.type === ArtefactType.TABLE) {
          // Convert table data to Markdown table format
          const tableData = parseTableContent(artefact.content);

          if (!tableData) {
            throw new Error("Unable to parse table content");
          }

          // Add title as heading and then the table
          markdownContent = `# ${artefact.title}\n\n${tableToMarkdown(tableData.headers, tableData.rows)}`;
        } else if (artefact.type === ArtefactType.DOCUMENT || artefact.type === ArtefactType.CODE) {
          // For documents and code, use content directly
          markdownContent = artefact.content;
        } else {
          throw new Error("Artefact type not supported for Markdown export");
        }

        // Generate smart filename based on content
        const baseFilename = artefact.title.toLowerCase().replace(/[^a-z0-9]/g, "_");
        const timestamp = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
        const filename = `${baseFilename}_${timestamp}.md`;

        const blob = new Blob([markdownContent], { type: "text/markdown;charset=utf-8" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");

        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);

        toast.success(t("Markdown downloaded!"), {
          description: t("Saved as {{filename}}", { filename }),
        });
      } catch (error) {
        logger.error("❌ Failed to download Markdown", error);
        toast.danger(t("Download failed"), { description: t("Unable to download as Markdown") });
      }
    },
    [t],
  );

  const handleDownloadAsPDFText = useCallback(
    async (artefact: Artefact) => {
      // Show loading toast
      toast(t("Exporting to {{format}}...", { format: "PDF" }), {
        description: t("Creating selectable text PDF"),
      });

      try {
        // Create PDF with production-quality markdown rendering
        // Don't add artefact title as heading - the content typically contains
        // the title already, and adding it creates duplicate headings in exports
        const markdown = artefact.content;
        const doc = await renderMarkdownToPdf(markdown, {
          title: artefact.title,
          author: getAuthorName(),
          brandFontFamily: brand.fontFamily,
          brandCodeFontFamily: brand.codeFontFamily,
        });

        // Save the PDF
        const filename = generateFilename(artefact, "pdf");

        doc.save(filename);

        // Show success toast
        toast.success(t("{{format}} exported successfully", { format: "PDF" }), {
          description: t("Saved as {{filename}}", { filename }),
        });
      } catch (error) {
        logger.error("❌ Failed to export PDF (text)", error);
        toast.danger(t("Failed to export {{format}}", { format: "PDF" }), {
          description: t("Unable to export content as PDF"),
        });
      }
    },
    [t, brand.codeFontFamily, brand.fontFamily, getAuthorName],
  );

  // Enhanced multi-format export functions
  const handleDownloadAsPDFVisual = useCallback(
    async (artefact: Artefact) => {
      // Show loading toast
      toast(t("Exporting to {{format}}...", { format: "PDF" }), {
        description: t("Please wait while we prepare your download"),
      });

      // Track cleanup for temporary iframes (HTML artefacts with opaque origins)
      let iframeCleanup: (() => void) | null = null;

      try {
        // For HTML artefacts, capture the iframe
        if (artefact.type === ArtefactType.HTML) {
          // Resolve an accessible iframe (creates temp one if sandbox blocks contentDocument)
          const resolved = await resolveAccessibleIframe(artefact);

          iframeCleanup = resolved.cleanup;
          const element = resolved.iframe.contentDocument!.body;

          // Use html2pdf.js for high-quality PDF generation
          const opt = {
            margin: 10,
            filename: generateFilename(artefact, "pdf"),
            image: { type: "jpeg" as const, quality: 0.98 },
            html2canvas: {
              scale: 2,
              useCORS: true,
              logging: false,
              onclone: (clonedDoc: globalThis.Document) => {
                sanitizeUnsupportedColors(clonedDoc);
              },
            },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" as const },
            // Prevent word/element splitting across page boundaries
            pagebreak: {
              mode: ["avoid-all", "css", "legacy"] as const,
              avoid: ["p", "li", "h1", "h2", "h3", "h4", "h5", "h6", "tr", "blockquote", "pre"],
            },
          };

          await html2pdf().set(opt).from(element).save();
        } else if (artefact.type === ArtefactType.TABLE) {
          // For TABLE artefacts, capture only the table viewer wrapper.
          // ArtefactsList also sets `data-artefact-id` on its outer card (which
          // includes the title/preview header), so we use a table-specific
          // attribute to avoid capturing the surrounding chrome.
          const escapedId = escapeCssSelector(artefact.id);
          const tableContainer = document.querySelector(
            `[data-artefact-table-id="${escapedId}"]`,
          ) as HTMLElement;

          if (!tableContainer) {
            throw new Error("Table container not found for artefact");
          }

          // Measure the table's natural dimensions so html2canvas renders
          // against a viewport wide enough to fit every column. Without
          // `windowWidth`, html2canvas uses the browser viewport and clips
          // wide tables on the right edge of the resulting canvas. We also
          // flip to landscape when the table is much wider than tall —
          // many-column tables are unreadable in portrait at A4 width.
          const tableElement = tableContainer.querySelector("table") as HTMLElement | null;
          const captureWidth = tableElement
            ? Math.max(tableElement.scrollWidth, tableElement.offsetWidth, 800)
            : Math.max(tableContainer.scrollWidth, 800);
          const captureHeight = tableElement
            ? Math.max(tableElement.scrollHeight, tableElement.offsetHeight, 400)
            : Math.max(tableContainer.scrollHeight, 400);
          const orientation: "portrait" | "landscape" =
            captureWidth > captureHeight * 1.2 ? "landscape" : "portrait";

          // Use html2pdf.js for high-quality PDF generation with table preserved
          const opt = {
            margin: 10,
            filename: generateFilename(artefact, "pdf"),
            image: { type: "jpeg" as const, quality: 0.98 },
            html2canvas: {
              scale: 3,
              backgroundColor: "#ffffff",
              useCORS: true,
              logging: false,
              allowTaint: false,
              removeContainer: true,
              imageTimeout: 15000,
              windowWidth: captureWidth,
              windowHeight: captureHeight,
              onclone: (clonedDoc: globalThis.Document) => {
                sanitizeUnsupportedColors(clonedDoc);

                // Find and remove max-height, overflow, and width constraints from the cloned element
                const clonedContainer = clonedDoc.querySelector(
                  `[data-artefact-table-id="${escapedId}"]`,
                ) as HTMLElement;

                if (clonedContainer) {
                  // Remove all constraints from the container and its children
                  const allElements = clonedContainer.querySelectorAll("*");

                  allElements.forEach((el) => {
                    const element = el as HTMLElement;

                    // Remove height constraints
                    element.style.maxHeight = "none";
                    element.style.height = "auto";

                    // Remove overflow constraints
                    element.style.overflow = "visible";
                    element.style.overflowX = "visible";
                    element.style.overflowY = "visible";

                    // Remove width constraints that truncate content
                    if (element.classList.contains("truncate")) {
                      element.style.maxWidth = "none";
                      element.style.overflow = "visible";
                      element.style.textOverflow = "clip";
                      element.style.whiteSpace = "normal";
                    }

                    // Ensure table cells show full content
                    if (element.tagName === "TD" || element.tagName === "TH") {
                      element.style.maxWidth = "none";
                      element.style.whiteSpace = "nowrap";
                    }
                  });

                  // Update the container itself
                  clonedContainer.style.maxHeight = "none";
                  clonedContainer.style.height = "auto";
                  clonedContainer.style.overflow = "visible";
                  clonedContainer.style.width = "auto";
                  clonedContainer.style.minWidth = "fit-content";

                  // Add vertical borders to table cells for PDF readability
                  const tableCells = clonedContainer.querySelectorAll("td, th");

                  tableCells.forEach((cell) => {
                    const cellEl = cell as HTMLElement;

                    // Apply light gray border (default-200) to right side
                    cellEl.style.borderRight = "1px solid #E4E4E7";

                    // Remove right border from the last cell in each row
                    if (!cellEl.nextElementSibling) {
                      cellEl.style.borderRight = "none";
                    }
                  });
                }
              },
            },
            jsPDF: { unit: "mm", format: "a4", orientation },
            // Prevent table rows from being split across page boundaries
            pagebreak: {
              mode: ["avoid-all", "css", "legacy"] as const,
              avoid: ["tr", "thead", "tbody", "th", "td"],
            },
          };

          await html2pdf().set(opt).from(tableContainer).save();
        } else if (artefact.type === ArtefactType.CODE) {
          // For CODE artefacts, capture the syntax-highlighted DOM element
          const escapedId = escapeCssSelector(artefact.id);
          const codeElement = document.querySelector(
            `[data-artefact-id="${escapedId}"] .syntax-highlighter`,
          ) as HTMLElement;

          if (!codeElement) {
            throw new Error("Code element not found for artefact");
          }

          // Use html2pdf.js for high-quality PDF generation with syntax highlighting preserved
          const opt = {
            margin: 10,
            filename: generateFilename(artefact, "pdf"),
            image: { type: "jpeg" as const, quality: 0.98 },
            html2canvas: {
              scale: 3, // High DPI for crisp code text
              backgroundColor: "#ffffff",
              useCORS: true,
              logging: false,
              allowTaint: false,
              removeContainer: true,
              imageTimeout: 15000,
              onclone: (clonedDoc: globalThis.Document) => {
                sanitizeUnsupportedColors(clonedDoc);
              },
            },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" as const },
            // Prevent code blocks from being split across page boundaries
            pagebreak: {
              mode: ["avoid-all", "css", "legacy"] as const,
              avoid: ["pre", "code", ".syntax-highlighter"],
            },
          };

          await html2pdf().set(opt).from(codeElement).save();
        } else {
          // For other types, use text-based PDF
          await handleDownloadAsPDFText(artefact);

          return;
        }

        // Show success toast
        const filename = generateFilename(artefact, "pdf");

        toast.success(t("{{format}} exported successfully", { format: "PDF" }), {
          description: t("Saved as {{filename}}", { filename }),
        });
      } catch (error) {
        logger.error("❌ Failed to export PDF (visual)", error);
        toast.danger(t("Failed to export {{format}}", { format: "PDF" }), {
          description: t("Unable to export content as PDF"),
        });
      } finally {
        iframeCleanup?.();
      }
    },
    [t, handleDownloadAsPDFText],
  );

  const handleDownloadAsWord = useCallback(
    async (artefact: Artefact) => {
      // Show loading toast
      toast(t("Exporting to {{format}}...", { format: "Word" }), {
        description: t("Please wait while we prepare your document"),
      });

      try {
        // Generate content using the programmatic pipeline (handles all formatting)
        // Per-artefact calculated titles are intentionally omitted from exports
        const markdown = artefact.content;
        let blob = await convertMarkdownToDocx(markdown, {
          title: artefact.title,
          creator: brand.name,
          fontFamily: getExportFontFamily(),
          codeFontFamily: brand.codeFontFamily,
          pageSize: exportPageSize,
        });

        // Apply branded template if available — renders the entire document
        // via docxtemplater, replacing the programmatic content blob.
        // HTML/table artefacts are captured as images for injection.
        const template = await resolveTemplate("word", brand.name);

        if (template) {
          try {
            const data = prepareTemplateData([artefact], {
              title: artefact.title,
              author: getAuthorName(),
              brand: brand.name,
            });

            let imageMap:
              | Map<string, import("@/utils/artefactExport/htmlToImage").CapturedImage>
              | undefined;

            if (artefact.type === ArtefactType.HTML) {
              try {
                const { captureHtmlArtefactsAsImages, createImageMap } =
                  await import("@/utils/artefactExport/htmlToImage");
                const captured = await captureHtmlArtefactsAsImages([artefact]);

                imageMap = createImageMap(captured);
              } catch {
                // Image capture failed — fall through to text-only template
              }
            }

            blob = await applyWordTemplate(template.data, data, imageMap);
          } catch (templateError) {
            logger.warn("Template rendering failed, using programmatic content", {
              error: String(templateError),
            });
          }
        }

        // Create download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        const filename = generateFilename(artefact, "docx");

        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);

        // Show success toast
        toast.success(t("{{format}} exported successfully", { format: "Word" }), {
          description: t("Saved as {{filename}}", { filename }),
        });
      } catch (error) {
        logger.error("❌ Failed to export Word", error);
        toast.danger(t("Failed to export {{format}}", { format: "Word" }), {
          description: t("Unable to export content as Word document"),
        });
      }
    },
    [t, brand.name, brand.codeFontFamily, exportPageSize, getExportFontFamily, getAuthorName],
  );

  const handleDownloadAsWordVisual = useCallback(
    async (artefact: Artefact) => {
      // Show loading toast
      toast(t("Exporting to {{format}}...", { format: "Word" }), {
        description: t("Creating visual document"),
      });

      // Track cleanup for temporary iframes (HTML artefacts with opaque origins)
      let iframeCleanup: (() => void) | null = null;

      try {
        let canvas: HTMLCanvasElement;

        // Handle HTML artefacts (iframe-based)
        if (artefact.type === ArtefactType.HTML) {
          // Resolve an accessible iframe (creates temp one if sandbox blocks contentDocument)
          const resolved = await resolveAccessibleIframe(artefact);

          iframeCleanup = resolved.cleanup;
          const doc = resolved.iframe.contentDocument!;

          canvas = await html2canvas(doc.body, {
            scale: 3,
            backgroundColor: "#ffffff",
            useCORS: true,
            logging: false,
            allowTaint: false,
            removeContainer: true,
            imageTimeout: 15000,
            windowWidth: doc.body.scrollWidth,
            windowHeight: doc.body.scrollHeight,
            // Sanitize unsupported CSS colour functions before capture
            onclone: (clonedDoc: globalThis.Document) => {
              sanitizeUnsupportedColors(clonedDoc);
            },
          });
        } else if (artefact.type === ArtefactType.TABLE) {
          // Handle TABLE artefacts (DOM-based) - capture the table viewer wrapper.
          // ArtefactsList's outer card also has `data-artefact-id`, so we use
          // the table-specific attribute to skip the title/preview header.
          const escapedId = escapeCssSelector(artefact.id);
          const tableContainer = document.querySelector(
            `[data-artefact-table-id="${escapedId}"]`,
          ) as HTMLElement;

          if (!tableContainer) {
            throw new Error("Table container not found for artefact");
          }

          canvas = await html2canvas(tableContainer, {
            scale: 3,
            backgroundColor: "#ffffff",
            useCORS: true,
            logging: false,
            allowTaint: false,
            removeContainer: true,
            imageTimeout: 15000,
            // Sanitize unsupported CSS colour functions before capture
            onclone: (clonedDoc: globalThis.Document) => {
              sanitizeUnsupportedColors(clonedDoc);
            },
          });
        } else {
          throw new Error("Visual export not supported for this artefact type");
        }

        // 2. Convert canvas to Uint8Array for docx ImageRun
        const blob = await new Promise<Blob>((resolve) => {
          canvas.toBlob((b) => resolve(b!), "image/png");
        });
        const arrayBuffer = await blob.arrayBuffer();
        const imageData = new Uint8Array(arrayBuffer);

        // 3. Calculate dimensions (A4 width = 600px, maintain aspect ratio)
        const maxWidth = 600;
        const imageHeight = Math.round(canvas.height * (maxWidth / canvas.width));

        // 4. Create Word document with embedded image
        // Per-artefact calculated titles are intentionally omitted from exports
        const doc = new Document({
          sections: [
            {
              properties: {},
              children: [
                new Paragraph({
                  children: [
                    new ImageRun({
                      data: imageData,
                      transformation: {
                        width: maxWidth,
                        height: imageHeight,
                      },
                      type: "png",
                    }),
                  ],
                }),
              ],
            },
          ],
        });

        // 5. Generate and download
        const docBlob = await Packer.toBlob(doc);
        const url = URL.createObjectURL(docBlob);
        const link = document.createElement("a");
        const filename = generateFilename(artefact, "docx");

        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);

        // Success toast
        toast.success(t("{{format}} exported successfully", { format: "Word" }), {
          description: t("Saved as {{filename}}", { filename }),
        });
      } catch (error) {
        logger.error("❌ Failed to export Word (visual)", error);
        toast.danger(t("Failed to export {{format}}", { format: "Word" }), {
          description: t("Unable to export visual Word document"),
        });
      } finally {
        iframeCleanup?.();
      }
    },
    [t],
  );

  const handleDownloadAsPowerPoint = useCallback(
    async (artefact: Artefact) => {
      // Show loading toast
      toast(t("Exporting to {{format}}...", { format: "PowerPoint" }), {
        description: t("Creating PowerPoint presentation"),
      });

      try {
        const filename = generateFilename(artefact, "pptx");

        // Capture HTML/table artefacts as images before template resolution
        let pptxImageMap:
          | Map<string, { imageData: Uint8Array; width: number; height: number }>
          | undefined;

        if (artefact.type === ArtefactType.HTML || artefact.type === ArtefactType.TABLE) {
          try {
            const { captureHtmlArtefactsAsImages, createImageMap, CAPTURE_WIDTH_POWERPOINT } =
              await import("@/utils/artefactExport/htmlToImage");
            const { markdownToHtml } = await import("@/utils/markdownConverter");

            // For tables, convert markdown to styled HTML first
            const captureArtefact: Artefact =
              artefact.type === ArtefactType.TABLE
                ? {
                    ...artefact,
                    type: ArtefactType.HTML,
                    content: `<html><head><style>
                      body { font-family: Arial, sans-serif; margin: 20px; color: #1a1a1a; }
                      table { border-collapse: collapse; width: 100%; }
                      th, td { border: 1px solid #ddd; padding: 8px 12px; text-align: left; font-size: 13px; }
                      th { background-color: #28326E; color: white; font-weight: bold; }
                      tr:nth-child(even) { background-color: #f5f5f8; }
                    </style></head><body>${markdownToHtml(artefact.content)}</body></html>`,
                  }
                : artefact;

            const capturedImages = await captureHtmlArtefactsAsImages([captureArtefact], {
              width: CAPTURE_WIDTH_POWERPOINT,
            });
            const imgMap = createImageMap(capturedImages);
            const captured = imgMap.get(artefact.id);

            if (captured) {
              pptxImageMap = new Map([
                [
                  artefact.id,
                  {
                    imageData: captured.imageData,
                    width: captured.width,
                    height: captured.height,
                  },
                ],
              ]);
            }
          } catch (imgError) {
            logger.warn("Failed to capture artefact image for PPTX template", {
              error: String(imgError),
            });
          }
        }

        // Try template-based export first
        const template = await resolveTemplate("pptx", brand.name);

        if (template) {
          // Per-artefact calculated titles are intentionally omitted from exports
          const data = preparePptxTemplateData([artefact], {
            title: undefined,
            author: getAuthorName(),
            brand: brand.name,
            fontFamily: getExportFontFamily(),
            imageMap: pptxImageMap,
          });

          const blob = await applyPptxTemplate(template.data, data);
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");

          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        } else {
          // Fall back to programmatic export
          // Per-artefact calculated titles are intentionally omitted from exports
          const pptx = await convertMarkdownToPptxSlides(artefact.content, {
            title: undefined,
            fontFamily: getExportFontFamily(),
            codeFontFamily: brand.codeFontFamily,
          });

          pptx.writeFile({ fileName: filename });
        }

        // Show success toast
        toast.success(t("{{format}} exported successfully", { format: "PowerPoint" }), {
          description: t("Saved as {{filename}}", { filename }),
        });
      } catch (error) {
        logger.error("❌ Failed to export PowerPoint", error);
        toast.danger(t("Failed to export {{format}}", { format: "PowerPoint" }), {
          description: t("Unable to export content as PowerPoint"),
        });
      }
    },
    [t, brand.name, brand.codeFontFamily, getExportFontFamily, getAuthorName],
  );

  const handleDownloadAsPowerPointTable = useCallback(
    async (artefact: Artefact) => {
      // Show loading toast
      toast(t("Exporting to {{format}}...", { format: "PowerPoint" }), {
        description: t("Creating table presentation"),
      });

      try {
        if (artefact.type !== ArtefactType.TABLE) {
          throw new Error("Artefact is not a table");
        }

        // Parse table content using existing utility
        const tableData = parseTableContent(artefact.content);

        if (!tableData) {
          throw new Error("Failed to parse table data");
        }

        // Get font family from user preference or brand
        const fontFamily = getExportFontFamily();

        // Create PowerPoint with table
        const pptx = new PptxGenJS();

        // Title slide
        const titleSlide = pptx.addSlide();

        titleSlide.addText(artefact.title, {
          x: 0.5,
          y: 1.0,
          w: 9,
          h: 1,
          fontSize: 32,
          bold: true,
          align: "center",
          fontFace: fontFamily,
        });

        // Table slide
        const tableSlide = pptx.addSlide();
        const rows = [
          tableData.headers,
          ...tableData.rows.map((row) => tableData.headers.map((h) => (row[h] || "").toString())),
        ];

        tableSlide.addTable(rows, {
          x: 0.5,
          y: 1.0,
          w: 9,
          colW: rows[0].map(() => 9 / rows[0].length),
          border: { pt: 1, color: "CCCCCC" },
          fill: { color: "F7F7F7" },
          fontSize: 10,
          fontFace: fontFamily,
        });

        // Save
        const filename = generateFilename(artefact, "pptx");

        pptx.writeFile({ fileName: filename });

        // Show success toast
        toast.success(t("{{format}} exported successfully", { format: "PowerPoint" }), {
          description: t("Saved as {{filename}}", { filename }),
        });
      } catch (error) {
        logger.error("❌ Failed to export PowerPoint table", error);
        toast.danger(t("Failed to export {{format}}", { format: "PowerPoint" }), {
          description: t("Unable to export table as PowerPoint"),
        });
      }
    },
    [t, getExportFontFamily],
  );

  const handleDownloadAsPowerPointVisual = useCallback(
    async (artefact: Artefact) => {
      // Show loading toast
      toast(t("Exporting to {{format}}...", { format: "PowerPoint" }), {
        description: t("Capturing visual content"),
      });

      // Track cleanup for temporary iframes (HTML artefacts with opaque origins)
      let iframeCleanup: (() => void) | null = null;

      try {
        let canvas: HTMLCanvasElement;

        // Handle HTML artefacts (iframe-based)
        if (artefact.type === ArtefactType.HTML) {
          // Resolve an accessible iframe (creates temp one if sandbox blocks contentDocument)
          const resolved = await resolveAccessibleIframe(artefact);

          iframeCleanup = resolved.cleanup;
          const doc = resolved.iframe.contentDocument!;

          canvas = await html2canvas(doc.body, {
            scale: 3,
            backgroundColor: "#ffffff",
            useCORS: true,
            logging: false,
            allowTaint: false,
            removeContainer: true,
            imageTimeout: 15000,
            windowWidth: doc.body.scrollWidth,
            windowHeight: doc.body.scrollHeight,
            // Sanitize unsupported CSS colour functions before capture
            onclone: (clonedDoc: globalThis.Document) => {
              sanitizeUnsupportedColors(clonedDoc);
            },
          });
        } else if (artefact.type === ArtefactType.TABLE) {
          // Handle TABLE artefacts (DOM-based) - capture the table viewer wrapper.
          // ArtefactsList's outer card also has `data-artefact-id`, so we use
          // the table-specific attribute to skip the title/preview header.
          const escapedId = escapeCssSelector(artefact.id);
          const tableContainer = document.querySelector(
            `[data-artefact-table-id="${escapedId}"]`,
          ) as HTMLElement;

          if (!tableContainer) {
            throw new Error("Table container not found for artefact");
          }

          canvas = await html2canvas(tableContainer, {
            scale: 3,
            backgroundColor: "#ffffff",
            useCORS: true,
            logging: false,
            allowTaint: false,
            removeContainer: true,
            imageTimeout: 15000,
            // Sanitize unsupported CSS colour functions before capture
            onclone: (clonedDoc: globalThis.Document) => {
              sanitizeUnsupportedColors(clonedDoc);
            },
          });
        } else {
          throw new Error("Visual export not supported for this artefact type");
        }

        // Convert to base64
        const base64Image = canvas.toDataURL("image/png");

        // Get font family from user preference or brand
        const fontFamily = getExportFontFamily();

        // Create PowerPoint with image
        const pptx = new PptxGenJS();

        // Title slide
        const titleSlide = pptx.addSlide();

        titleSlide.addText(artefact.title, {
          x: 0.5,
          y: 1.0,
          w: 9,
          h: 1,
          fontSize: 32,
          bold: true,
          align: "center",
          fontFace: fontFamily,
        });

        // Image slide
        const imageSlide = pptx.addSlide();
        const aspectRatio = canvas.height / canvas.width;
        const imageWidth = 9;
        const imageHeight = imageWidth * aspectRatio;

        imageSlide.addImage({
          data: base64Image,
          x: 0.5,
          y: Math.max(0.5, (6.5 - imageHeight) / 2),
          w: imageWidth,
          h: Math.min(imageHeight, 6.5),
        });

        // Save
        const filename = generateFilename(artefact, "pptx");

        pptx.writeFile({ fileName: filename });

        // Show success toast
        toast.success(t("{{format}} exported successfully", { format: "PowerPoint" }), {
          description: t("Saved as {{filename}}", { filename }),
        });
      } catch (error) {
        logger.error("❌ Failed to export PowerPoint (visual)", error);
        toast.danger(t("Failed to export {{format}}", { format: "PowerPoint" }), {
          description: t("Unable to export visual PowerPoint"),
        });
      } finally {
        iframeCleanup?.();
      }
    },
    [t, getExportFontFamily],
  );

  const handleDownloadAsExcel = useCallback(
    async (artefact: Artefact) => {
      // Show loading toast
      toast(t("Exporting to {{format}}...", { format: "Excel" }), {
        description: t("Preparing export"),
      });

      try {
        // For HTML artefacts (charts), capture as image so it can be embedded
        // in Excel rather than falling back to placeholder text.
        let imageMap:
          | Map<string, import("@/utils/artefactExport/htmlToImage").CapturedImage>
          | undefined;

        if (artefact.type === ArtefactType.HTML) {
          try {
            const { captureHtmlArtefactsAsImages, createImageMap, PAGE_CAPTURE_WIDTHS } =
              await import("@/utils/artefactExport/htmlToImage");
            const captured = await captureHtmlArtefactsAsImages([artefact], {
              width: PAGE_CAPTURE_WIDTHS.A4,
            });

            imageMap = createImageMap(captured);
          } catch (error) {
            logger.warn("Failed to capture chart image for Excel export", {
              error: String(error),
            });
            // Continue without image — Excel will show placeholder text
          }
        }

        // Use batch export which supports all artefact types:
        // - TABLE: proper Excel tables with formatting
        // - DOCUMENT: markdown stripped to text cells
        // - HTML: captured as images
        // - CODE: code blocks with monospace font
        await batchExportToExcel(
          [artefact],
          {
            useSeparateWorksheets: false,
            fontFamily: getExportFontFamily(),
            codeFontFamily: brand.codeFontFamily,
            labels: {
              exportedContent: t("Exported Content"),
              overview: t("Overview"),
              artefactColumn: t("Artefact"),
              typeColumn: t("Type"),
              createdColumn: t("Created"),
            },
          },
          artefact.title,
          brand,
          theme,
          imageMap,
        );

        // Show success toast
        const filename = generateFilename(artefact, "xlsx");

        toast.success(t("{{format}} exported successfully", { format: "Excel" }), {
          description: t("Saved as {{filename}}", { filename }),
        });
      } catch (error) {
        logger.error("❌ Failed to export Excel", error);
        toast.danger(t("Failed to export {{format}}", { format: "Excel" }), {
          description: t("Unable to export content as Excel"),
        });
      }
    },
    [t, brand, theme, getExportFontFamily],
  );

  const handleDownloadAsInteractiveHTML = useCallback(
    async (artefact: Artefact) => {
      // Show loading toast
      toast(t("Exporting to {{format}}...", { format: "Interactive HTML" }), {
        description: t("Creating standalone HTML file"),
      });

      try {
        if (artefact.type !== ArtefactType.HTML) {
          throw new Error("Artefact is not HTML");
        }

        // Create standalone HTML with metadata
        const timestamp = new Date().toLocaleString();
        const standaloneHTML = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${escapeHtml(artefact.title)}</title>
    <meta name="generator" content="Pathway AI Platform">
    <meta name="created" content="${timestamp}">
    <style>
        body {
            font-family: system-ui, -apple-system, sans-serif;
            padding: 20px;
            max-width: 1200px;
            margin: 0 auto;
        }
        h1 {
            color: #333;
            border-bottom: 2px solid #0070f3;
            padding-bottom: 10px;
        }
        .metadata {
            color: #666;
            font-size: 0.9em;
            margin-bottom: 20px;
        }
    </style>
    <!-- Chart.js CDN (if needed) -->
    <script src="https://cdn.jsdelivr.net/npm/chart.js"></script>
    <!-- Plotly CDN (if needed) -->
    <script src="https://cdn.plot.ly/plotly-latest.min.js"></script>
</head>
<body>
    <h1>${escapeHtml(artefact.title)}</h1>
    <div class="metadata">
        Generated: ${timestamp} | Source: Pathway AI Platform
    </div>
    ${artefact.content}
</body>
</html>`;

        // Create download
        const blob = new Blob([standaloneHTML], { type: "text/html;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        const filename = generateFilename(artefact, "html");

        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);

        // Show success toast
        toast.success(t("{{format}} exported successfully", { format: "Interactive HTML" }), {
          description: t("Saved as {{filename}}", { filename }),
        });
      } catch (error) {
        logger.error("❌ Failed to export Interactive HTML", error);
        toast.danger(t("Failed to export {{format}}", { format: "Interactive HTML" }), {
          description: t("Unable to export content as Interactive HTML"),
        });
      }
    },
    [t],
  );

  const handleDownloadAsHTMLSource = useCallback(
    async (artefact: Artefact) => {
      try {
        if (artefact.type !== ArtefactType.HTML) {
          throw new Error("Artefact is not HTML");
        }

        const blob = new Blob([artefact.content], { type: "text/html;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        const filename = generateFilename(artefact, "html");

        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);

        toast.success(t("HTML source downloaded!"), {
          description: t("Saved as {{filename}}", { filename }),
        });
      } catch (error) {
        logger.error("❌ Failed to download HTML source", error);
        toast.danger(t("Download failed"), { description: t("Unable to download HTML source") });
      }
    },
    [t],
  );

  // Unified download handler with format support
  const handleDownloadWithFormat = useCallback(
    async (artefact: Artefact, format: DownloadFormat) => {
      switch (format) {
        case "png":
          return handleDownloadAsImage(artefact);
        case "pdf-visual":
          return handleDownloadAsPDFVisual(artefact);
        case "pdf-text":
          return handleDownloadAsPDFText(artefact);
        case "excel":
          return handleDownloadAsExcel(artefact);
        case "csv":
          return handleDownloadAsCSV(artefact);
        case "word":
          return handleDownloadAsWord(artefact);
        case "word-visual":
          return handleDownloadAsWordVisual(artefact);
        case "powerpoint":
          // For tables, use data-based PowerPoint export with table
          if (artefact.type === ArtefactType.TABLE) {
            return handleDownloadAsPowerPointTable(artefact);
          }

          return handleDownloadAsPowerPoint(artefact);
        case "powerpoint-visual":
          return handleDownloadAsPowerPointVisual(artefact);
        case "html-source":
          return handleDownloadAsHTMLSource(artefact);
        case "html-interactive":
          return handleDownloadAsInteractiveHTML(artefact);
        case "markdown":
          return handleDownloadAsMarkdown(artefact);
        case "text":
          return handleDownload(artefact);
        default:
          return handleDownload(artefact);
      }
    },
    [
      handleDownload,
      handleDownloadAsImage,
      handleDownloadAsPDFVisual,
      handleDownloadAsPDFText,
      handleDownloadAsExcel,
      handleDownloadAsCSV,
      handleDownloadAsWord,
      handleDownloadAsWordVisual,
      handleDownloadAsPowerPoint,
      handleDownloadAsPowerPointTable,
      handleDownloadAsPowerPointVisual,
      handleDownloadAsHTMLSource,
      handleDownloadAsInteractiveHTML,
      handleDownloadAsMarkdown,
    ],
  );

  return {
    handleCopy,
    handleCopyPlain,
    handleCopyWithFilename,
    handleCopyWithMetadata,
    handleDownload,
    handleRefresh,
    formatTimestamp,
    // Table-specific exports
    handleCopyAsCSV,
    handleCopyAsJSON,
    handleDownloadAsCSV,
    // Document-specific exports
    handleCopyAsMarkdown,
    handleDownloadAsMarkdown,
    // HTML-specific exports
    handleCopyAsImage,
    handleDownloadAsImage,
    // Enhanced multi-format exports
    handleDownloadAsPDFVisual,
    handleDownloadAsPDFText,
    handleDownloadAsWord,
    handleDownloadAsWordVisual,
    handleDownloadAsPowerPoint,
    handleDownloadAsPowerPointTable,
    handleDownloadAsPowerPointVisual,
    handleDownloadAsExcel,
    handleDownloadAsInteractiveHTML,
    handleDownloadAsHTMLSource,
    handleDownloadWithFormat,
  };
};
